import gql from 'graphql-tag';
import { FRAGMENT_BOOKING } from './booking';

export const FRAGMENT_PROPERTY = gql`
  fragment fragmentQueryProperty on Property {
    id
    originId
    name
    status
    imageName
    postalCode
    addressLine1
    addressLine2
    contactEmailAddress
    contactAreaCode
    contactPhoneNumber
    managementCompanyName
    billingCycle
    currency
    areaUnitOfMeasurement
    ownerName
    country {
      id
      name
      slug
      countryCode
    }
    city {
      id
      name
      slug
      latitude
      longitude
    }
    country {
      id
      slug
      name
    }
    maxNumOfBedsInFlat(roomTypeId: $roomTypeId)
    buildings {
      id
      originId
      name
      floors {
        id
        originId
        name
        index
      }
    }
    roomTypes(availableForBook: true) {
      id
      originId
      name
      kitchenArrangement
      bathroomArrangement
      category
      hasPrice
    }
    flatPreferencePlanSetting {
      smokingEnabled
      petEnabled
      dietaryEnabled
      genderEnabled
    }
    paymentSettings {
      paymentChannel
      paymentMethod
      isEnabled
    }
  }
`;

export const QUERY_NODE_PROPERTY = gql`
  query SP_QueryNodeProperty($id: ID, $roomTypeId: ID!) {
    node(id: $id) {
      ... on Property {
        ...fragmentQueryProperty
      }
    }
  }
  ${FRAGMENT_PROPERTY}
`;

export const QUERY_AVAIABLE_TENANCY_OPTIONS = gql`
  query SP_QueryAvaiableTenancyOptions($propertyId: ID!, $input: getAvailableTenancyOptionsInput!) {
    getAvailableTenancyOptions(propertyId: $propertyId, input: $input) {
      availableTenancyOptions {
        selectableStartDates
        tenancyOption {
          id
          originId
          name
          rateType
          tenancyLengthType
          tenancyType
          startDate
          endDate
          startDateType
          endDateType
          tenancyLength
          tenancyLengthOption
          tenancyLengthUnit
          intervalForTurnaround
          academicYear {
            id
            originId
            name
            fromYear
            toYear
          }
        }
      }
    }
  }
`;

export const QUERY_AVAIABLE_END_DATE_FOR_TENANCY_OPTIONS = gql`
  query SP_QueryAvaiableEndDateForTenancyOptions(
    $input: getAvailableEndDatesForTenancyOptionInput!
  ) {
    getAvailableEndDatesForTenancyOption(input: $input)
  }
`;

const FRAGMENT_FLOOR = gql`
  fragment fragmentQueryFloor on Floor {
    id
    originId
    index
    name
    floorPlanFilePath
  }
`;

const FRAGMENT_BUILDING = gql`
  fragment fragmentQueryBuilding on Building {
    id
    originId
    name
  }
`;

const FRAGMENT_FLAT = gql`
  fragment fragmentQueryFlat on Flat {
    id
    originId
    name
    kitchenOrLaundryArea
    bathroomArrangement
    bathroomType
    kitchenArrangement
    kitchenType
  }
`;

export const QUERY_FLAT_FOR_SELECTION = gql`
  query SP_QueryStudentGetFlatsForSelection(
    $propertyId: ID!
    $filter: studentGetFlatsForSelectionInput!
    $pageNumber: Int = 1
    $pageSize: Int = 10
  ) {
    studentGetFlatsForSelection(
      propertyId: $propertyId
      filter: $filter
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      floors {
        floor {
          ...fragmentQueryFloor
        }
        building {
          ...fragmentQueryBuilding
        }
        flats {
          flat {
            ...fragmentQueryFlat
          }
          availableNumOfBedsInFlat
          dietaryPreference
          disabilityAccess
          genderPreference
          marketingInstallmentNumber
          marketingPrice
          minPriceForBillingCycle
          minPricePerNight
          minPricePerMonth
          petPreference
          smokingPreference
          totalNumOfBedsInFlat
        }
        billingCycle
      }
      total
      totalPage
      pageNumber
      pageSize
    }
  }
  ${FRAGMENT_FLOOR}
  ${FRAGMENT_BUILDING}
  ${FRAGMENT_FLAT}
`;

export const QUERY_SELECT_FALT_INFO = gql`
  query SP_QueryStudentGetSelectedFlatInfo(
    $propertyId: ID!
    $filter: studentGetSelectedFlatInfoInput!
  ) {
    studentGetSelectedFlatInfo(propertyId: $propertyId, filter: $filter) {
      beds {
        bedId
        bedName
        pricePerNight
        pricePerNightOriginal
        pricePerMonth
        pricePerMonthOriginal
        available
        bedStatus
        maxOccupancy
        disabilityAccess
        bedTypeName
        bedLength
        bedWidth
        lengthUnitOfMeasurement
        areaUnitOfMeasurement
        roomSize
        matchFilters
        totalPriceOriginal
        priceForBillingCycle
        marketingPrice
        marketingInstallmentNumber
        tenant {
          gender
          age
          yearOfStudy
        }
      }
    }
  }
`;

export const MUTATION_SELECT_BED = gql`
  mutation SP_StudentSelectBed(
    $propertyId: ID!
    $input: selectBedInput!
    $editBookingInput: studentEditBookingWhenSelectBedInput
  ) {
    studentSelectBed(propertyId: $propertyId, input: $input, editBookingInput: $editBookingInput) {
      ...fragmentQueryBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const QUERY_GET_RENT_TAX = gql`
  query SP_QueryStudentGetRentTax($filter: GetRentTaxFilterInput!) {
    getRentTax(filter: $filter) {
      taxIncludedInPrice
      taxRate {
        taxRate
      }
    }
  }
`;

export const QUERY_NODE_ROOMTYPE_SERVEICES = gql`
  query SP_QueryNodeRoomtypeServices($id: ID!, $input: serviceOnRoomTypeInput!) {
    node(id: $id) {
      ... on RoomType {
        services(input: $input) {
          id
          name
          serviceType
          serviceOptions {
            id
            name
            description
            isDefault
          }
        }
      }
    }
  }
`;

export const QUERY_GET_SERVICE_OPTION_FOR_SELECTION = gql`
  query SP_QueryGetServiceOptionForSelection(
    $propertyId: ID!
    $input: getServiceOptionsForSelectionInput!
  ) {
    getServiceOptionsForSelection(propertyId: $propertyId, input: $input) {
      services {
        id
        name
        serviceType
        incrementType
        serviceOptions {
          id
          name
          isDefault
          description
          originalPriceContribution
        }
      }
    }
  }
`;

export const QUERY_GET_SERVICE_OPTION_INFO = gql`
  query SP_QueryGetServiceOptionInfo(
    $propertyId: ID!
    $input: getSelectedServiceOptionsInfoInput!
  ) {
    getSelectedServiceOptionsInfo(propertyId: $propertyId, input: $input) {
      pricePerNight
      priceForBillingCycle
      totalPrice
    }
  }
`;

export const MUTATION_SELECT_SERVICE = gql`
  mutation SP_StudentSelectSerive($propertyId: ID!, $input: selectServiceOptionsInput!) {
    studentSelectServices(propertyId: $propertyId, input: $input) {
      id
      status
      serviceSelections {
        id
        serviceId
        serviceName
        serviceOptionId
        serviceType
        serviceOptionName
      }
      totalPriceAfterService
      pricePerNightAfterService
      countDown
      installment {
        name
        installmentId
        installmentType
      }
      installmentPlans {
        dueDate
        amountDue
        isDueBeforeBookingConfirmed
        partOfRentAdditionalFeeDeduct
        partOfRentAdditionalFees {
          name
        }
      }
      checkOutCartInfo {
        totalAmountToBePaid
        paymentDueNow {
          amountDue
          details {
            name
            amount
            description
          }
        }
        priceBreakDown {
          totalPrice
          details {
            name
            amount
            description
            discountName
            discountValue
            taxInfo {
              name
              amount
              taxRate
              includedInPrice
            }
          }
          refundableDetails {
            name
            amount
            description
            discountName
            discountValue
            taxInfo {
              name
              amount
              taxRate
              includedInPrice
            }
          }
        }
      }
    }
  }
`;

export const QUERY_PROPERTY_ACADEMIC_YEAR_FLEXIBILITY_CONTROL_INFO = gql`
  query QueryPropertyAcademicYearFlexibilityControlInfo($id: ID) {
    node(id: $id) {
      ... on Property {
        id
        academicYear {
          current {
            id
            flexibilityControlInfo {
              displaySelectedServiceDuringBookingJourney
            }
          }
        }
      }
    }
  }
`;
